import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BacklogDripPanel } from '../components/BacklogDripPanel';

import { LeadsListPage } from './LeadsListPage';

import { PageShell } from '@/components/page-shell';
import { useUrlParamWriter } from '@/hooks/use-url-param-writer';

/**
 * Dedicated sidebar surface for the exit-unassigned queue: leads released
 * from users who were deactivated, deleted, or whose date-of-exit has
 * arrived. It drives the `?exitUnassigned=true` mode of `LeadsListPage`
 * (embedded, so this page owns the header). Server-side RBAC scopes the
 * rows per role - a SALES_LEAD sees only their direct reports' released
 * leads, an ADMIN sees the whole queue. Reachable only by holders of
 * `ops.exit_unassigned.read` (route guard + sidebar gate).
 */
export const ExitUnassignedLeadsPage = (): React.JSX.Element => {
  const [params] = useSearchParams();
  const writeParams = useUrlParamWriter();

  // Force the exit-unassigned mode on. The list reads this param to swap
  // its query + bulk actions; setting it here (instead of a prop) keeps a
  // single source of truth for the mode and lets shared links round-trip.
  useEffect(() => {
    if (params.get('exitUnassigned') === 'true') return;
    writeParams((p) => p.set('exitUnassigned', 'true'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageShell
      title="Unassigned leads"
      description="Leads released from users who were deactivated, deleted, or whose date-of-exit has arrived. Select rows and reassign them to an active agent."
    >
      {/* 2026-10-01. The queue is dealt out a few leads per agent per day
          by the backlog drip; this strip shows what is left, today's quota
          and (for admins) the controls. Manual bulk-assign below still works. */}
      <div className="mb-4">
        <BacklogDripPanel />
      </div>
      <LeadsListPage embedded />
    </PageShell>
  );
};
